import api from '@cannect/services/api'
import {
  TMyConversation,
  TMyTicketsResponse,
  TOpenConversationResponse,
  TSupportAttachment,
  TSupportConversation,
  TSupportMessage,
  TSupportTicketDetail
} from './types'

type TApiEnvelope<T> = { success: boolean; data: T; message?: string }

/** Abre ou reabre a conversa do pedido e já devolve o token do Firebase. */
export const openConversation = async (orderId: number) =>
  api
    .post<TApiEnvelope<TOpenConversationResponse>>('/support-chat/conversations', {
      order_id: orderId
    })
    .then(({ data }) => data.data)

export const getConversation = async (uuid: string) =>
  api.get<TApiEnvelope<TSupportConversation>>(`/support-chat/conversations/${uuid}`).then(({ data }) => data.data)

/** Pede atendimento humano: entra na fila. */
export const requestHuman = async (uuid: string) =>
  api
    .post<TApiEnvelope<TSupportConversation>>(`/support-chat/conversations/${uuid}/request-human`)
    .then(({ data }) => data.data)

export const closeConversation = async (uuid: string) =>
  api
    .post<TApiEnvelope<TSupportConversation>>(`/support-chat/conversations/${uuid}/close`)
    .then(({ data }) => data.data)

/**
 * Manda a mensagem do paciente pelo backend, não direto no Realtime Database.
 *
 * O RTDB aceitaria a escrita, mas quem conta as mensagens e atualiza
 * `last_message_at` no MySQL é o backend — e é aqui que a resposta do bot vai
 * ser disparada quando o fluxo do n8n entrar. A mensagem volta pela mesma
 * escuta do RTDB, então a tela não precisa fazer nada com o retorno.
 */
export const sendMessage = async ({
  uuid,
  content,
  attachments = []
}: {
  uuid: string
  content: string
  attachments?: TSupportAttachment[]
}) =>
  api
    .post<TApiEnvelope<TSupportMessage>>(`/support-chat/conversations/${uuid}/messages`, {
      content,
      attachments
    })
    .then(({ data }) => data.data)

/** Sobe um anexo e devolve a URL já assinada para exibir na conversa. */
export const uploadAttachment = async ({ uuid, file }: { uuid: string; file: File }) => {
  const formData = new FormData()
  formData.append('file', file)

  return api
    .post<
      TApiEnvelope<TSupportAttachment>
    >(`/support-chat/conversations/${uuid}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
    .then(({ data }) => data.data)
}

/**
 * Resposta da pesquisa de experiência. Aceita uma vez só.
 *
 * Três perguntas: satisfação (0 a 10), resolução do problema e facilidade.
 * Só a satisfação é obrigatória — o resto vai se o paciente responder.
 */
export const rateConversation = async ({
  uuid,
  score,
  comment,
  resolution,
  effortScore,
  effortComment
}: {
  uuid: string
  score: number
  comment?: string
  resolution?: 'total' | 'partial' | 'none'
  effortScore?: number
  effortComment?: string
}) =>
  api
    .post<TApiEnvelope<{ nps_score: number; nps_answered_at: string }>>(`/support-chat/conversations/${uuid}/rating`, {
      score,
      comment,
      resolution,
      effort_score: effortScore,
      effort_comment: effortComment
    })
    .then(({ data }) => data.data)

/**
 * Motivos de cancelamento, para o paciente escolher em vez de escrever.
 *
 * Vem do backend e não de uma cópia aqui: é a mesma lista que o relatório
 * soma depois.
 */
export const listCancellationReasons = async () =>
  api.get<TApiEnvelope<string[]>>('/support-chat/cancellation-reasons').then(({ data }) => data.data)

/** Minhas solicitações: o que o paciente acompanha. */
export const listMyTickets = async () =>
  api.get<TApiEnvelope<TMyTicketsResponse>>('/support-chat/my-tickets').then(({ data }) => data.data)

export const getMyTicket = async (uuid: string) =>
  api.get<TApiEnvelope<TSupportTicketDetail>>(`/support-chat/my-tickets/${uuid}`).then(({ data }) => data.data)

/** Conversas em aberto do paciente, com as não lidas. */
export const listMyConversations = async () =>
  api.get<TApiEnvelope<TMyConversation[]>>('/support-chat/my-conversations').then(({ data }) => data.data)

/** O paciente está com a conversa na tela: zera as não lidas dele. */
export const markConversationRead = async (uuid: string) =>
  api.post(`/support-chat/conversations/${uuid}/read`).then(({ data }) => data)

/** O paciente abriu a solicitação: zera as atualizações não lidas. */
export const markTicketRead = async (uuid: string) =>
  api.post(`/support-chat/my-tickets/${uuid}/read`).then(({ data }) => data)
